import axios from 'axios'

const state = {
  clientInfo: null,
  authorizationCode: null,
  loading: false,
  error: null,
  authorizing: false,
  denying: false
}

const getters = {
  getClientInfo: (state) => state.clientInfo,
  getAuthorizationCode: (state) => state.authorizationCode,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  isAuthorizing: (state) => state.authorizing,
  isDenying: (state) => state.denying
}

const mutations = {
  SET_CLIENT_INFO(state, clientInfo) {
    state.clientInfo = clientInfo
  },
  SET_AUTHORIZATION_CODE(state, code) {
    state.authorizationCode = code
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_AUTHORIZING(state, authorizing) {
    state.authorizing = authorizing
  },
  SET_DENYING(state, denying) {
    state.denying = denying
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  CLEAR_SSO_STATE(state) {
    state.clientInfo = null
    state.authorizationCode = null
    state.loading = false
    state.error = null
    state.authorizing = false
    state.denying = false
  }
}

const actions = {
  /**
   * Fetch client information from SSO server
   * @param {Object} context - Vuex context
   * @param {String} clientId - Client ID
   */
  async fetchClientInfo({ commit }, clientId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const baseUrl = process.env.VUE_APP_API_HOST || 'http://localhost:8020'
      const response = await axios.get(`${baseUrl}/api/sso/clients/${clientId}`)
      
      const clientData = response.data.data || response.data
      
      // Normalize the response to use consistent property names
      const normalizedClientInfo = {
        clientId: clientData.client_id || clientId,
        clientName: clientData.client_name || clientData.clientName || clientId,
        clientSecret: clientData.client_secret || clientData.clientSecret,
        redirectUris: clientData.redirect_uris || clientData.redirectUris || [],
        allowedOrigins: clientData.allowed_origins || clientData.allowedOrigins || [],
        autoApprove: clientData.auto_approve || clientData.autoApprove || false,
        scopes: clientData.scopes || []
      }
      
      commit('SET_CLIENT_INFO', normalizedClientInfo)
      commit('SET_LOADING', false)
      
      return normalizedClientInfo
    } catch (error) {
      console.error('Error fetching client info:', error)
      const errorMessage = error.response?.data?.message || 'Gagal memuat informasi client'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      
      // Return fallback client info
      const fallbackClientInfo = {
        clientId: clientId,
        clientName: clientId,
        redirectUris: [],
        allowedOrigins: [],
        autoApprove: false,
        scopes: []
      }
      commit('SET_CLIENT_INFO', fallbackClientInfo)
      
      throw error
    }
  },

  /**
   * Authorize client access and generate authorization code
   * @param {Object} context - Vuex context
   * @param {Object} payload - Authorization payload
   */
  async authorize({ commit }, { clientId, redirectUri, responseType, state }) {
    commit('SET_AUTHORIZING', true)
    commit('CLEAR_ERROR')
    
    try {
      const baseUrl = process.env.VUE_APP_API_HOST || 'http://localhost:8020'
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      
      if (!token) {
        throw new Error('Token tidak ditemukan. Silakan login kembali.')
      }
      
      const response = await axios.post(`${baseUrl}/api/sso/authorize`, {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType || 'code',
        state: state
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      const authCode = response.data.data?.code || response.data.code
      
      if (!authCode) {
        throw new Error('Kode otorisasi tidak ditemukan dalam response')
      }
      
      commit('SET_AUTHORIZATION_CODE', authCode)
      commit('SET_AUTHORIZING', false)
      
      return {
        code: authCode,
        state: state
      }
    } catch (error) {
      console.error('Error authorizing:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat memberikan otorisasi'
      commit('SET_ERROR', errorMessage)
      commit('SET_AUTHORIZING', false)
      throw error
    }
  },

  /**
   * Deny authorization request
   * @param {Object} context - Vuex context
   */
  async deny({ commit }) {
    commit('SET_DENYING', true)
    commit('CLEAR_ERROR')
    
    try {
      // Just set denying state, actual redirect will be handled in component
      commit('SET_DENYING', false)
      return { denied: true }
    } catch (error) {
      console.error('Error denying:', error)
      commit('SET_ERROR', 'Terjadi kesalahan saat menolak otorisasi')
      commit('SET_DENYING', false)
      throw error
    }
  },

  /**
   * Exchange authorization code for access token
   * @param {Object} context - Vuex context
   * @param {Object} payload - Token exchange payload
   */
  async exchangeToken({ commit }, { code, clientId, clientSecret, redirectUri }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const baseUrl = process.env.VUE_APP_API_HOST || 'http://localhost:8020'
      
      const response = await axios.post(`${baseUrl}/api/sso/token`, {
        grant_type: 'authorization_code',
        code: code,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri
      })
      
      const tokenData = response.data.data || response.data
      
      commit('SET_LOADING', false)
      
      return {
        accessToken: tokenData.access_token || tokenData.accessToken,
        tokenType: tokenData.token_type || tokenData.tokenType,
        expiresIn: tokenData.expires_in || tokenData.expiresIn,
        refreshToken: tokenData.refresh_token || tokenData.refreshToken,
        scope: tokenData.scope
      }
    } catch (error) {
      console.error('Error exchanging token:', error)
      const errorMessage = error.response?.data?.message || 'Gagal menukar kode otorisasi'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw error
    }
  },

  /**
   * Get user info using access token
   * @param {Object} context - Vuex context
   * @param {String} accessToken - Access token
   */
  async getUserInfo({ commit }, accessToken) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const baseUrl = process.env.VUE_APP_API_HOST || 'http://localhost:8020'
      
      const response = await axios.get(`${baseUrl}/api/sso/userinfo`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
      
      const userInfo = response.data.data || response.data
      
      commit('SET_LOADING', false)
      
      return userInfo
    } catch (error) {
      console.error('Error getting user info:', error)
      const errorMessage = error.response?.data?.message || 'Gagal memuat informasi pengguna'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw error
    }
  },

  /**
   * Refresh access token using refresh token
   * @param {Object} context - Vuex context
   * @param {Object} payload - Refresh token payload
   */
  async refreshToken({ commit }, { refreshToken, clientId, clientSecret }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const baseUrl = process.env.VUE_APP_API_HOST || 'http://localhost:8020'
      
      const response = await axios.post(`${baseUrl}/api/sso/token`, {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: clientId,
        client_secret: clientSecret
      })
      
      const tokenData = response.data.data || response.data
      
      commit('SET_LOADING', false)
      
      return {
        accessToken: tokenData.access_token || tokenData.accessToken,
        tokenType: tokenData.token_type || tokenData.tokenType,
        expiresIn: tokenData.expires_in || tokenData.expiresIn,
        refreshToken: tokenData.refresh_token || tokenData.refreshToken,
        scope: tokenData.scope
      }
    } catch (error) {
      console.error('Error refreshing token:', error)
      const errorMessage = error.response?.data?.message || 'Gagal memperbarui token'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw error
    }
  },

  /**
   * Revoke access token
   * @param {Object} context - Vuex context
   * @param {String} token - Token to revoke
   */
  async revokeToken({ commit }, token) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const baseUrl = process.env.VUE_APP_API_HOST || 'http://localhost:8020'
      
      await axios.post(`${baseUrl}/api/sso/revoke`, {
        token: token
      })
      
      commit('SET_LOADING', false)
      commit('CLEAR_SSO_STATE')
      
      return { revoked: true }
    } catch (error) {
      console.error('Error revoking token:', error)
      const errorMessage = error.response?.data?.message || 'Gagal mencabut token'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw error
    }
  },

  /**
   * Clear error state
   * @param {Object} context - Vuex context
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },

  /**
   * Clear all SSO state
   * @param {Object} context - Vuex context
   */
  clearState({ commit }) {
    commit('CLEAR_SSO_STATE')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
